import React, { useState, useEffect } from 'react'

import Scrollspy from 'react-scrollspy'

import { Button, IconButton, makeStyles, Drawer, AppBar, Toolbar, useMediaQuery } from '@paintscout/ui'
import type { WithStyles, MuiTheme as Theme } from '@paintscout/ui'

import HamburgerIcon from '@material-ui/icons/Menu'
import PresentationNavText from '../PresentationNavText'
import PresentationAcceptButton from '../PresentationAcceptButton'
import { usePresentationNav } from '../PresentationNavContext'
import { useQuote } from '../../../context'
import { getObjectLabels, getPresentationLogo, getPresentationNavItems } from '@paintscout/util/builder'
import CloseButton from '../../dialogs/common/CloseButton'
import type { PresentationOptionV2 } from 'paintscout'
import PresentationQuoteDrawer from '../PresentationQuoteDrawer'
import classnames from 'classnames'
import { useFrame } from 'react-frame-component'
import { useClientOptions } from '@paintscout/ui-business'
import { PRESENTATION_DEVICE_WIDTH } from '../../util'

export const useStyles = makeStyles<Theme>(
  (theme: Theme) => {
    return {
      drawerPanel: {
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      },
      drawerHeader: {
        flex: '0 0 auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(2, 2.5),
        borderBottom: `1px solid ${theme.palette.grey[300]}`
      },
      drawerTitle: {
        font: '600 13px Montserrat, sans-serif',
        color: theme.palette.grey[900],
        letterSpacing: '0.02em',
        textTransform: 'uppercase'
      },
      drawerCloseButton: {
        width: 26,
        height: 26,
        padding: 0,
        color: theme.palette.grey[600]
      },
      drawerBody: {
        flex: '1 1 auto',
        overflowY: 'auto',
        padding: theme.spacing(1, 0, 2)
      },
      appBar: {
        transition: 'height .1s cubic-bezier(0.46, 0.03, 0.52, 0.96), height 1s cubic-bezier(0.46, 0.03, 0.52, 0.96)',
        background: theme.palette.common.white,
        color: theme.palette.grey[700],
        border: 'none',
        borderBottom: `1px solid ${theme.palette.grey[300]}`,
        boxShadow: 'none',
        maxWidth: '100vw',
        boxSizing: 'border-box',
        justifyContent: 'center',
        '@media print': {
          display: 'none'
        },
        '&$shrunk': {
          '& $logoWrapper': {
            '& img': {
              maxWidth: 160,
              maxHeight: 89,
              [theme.breakpoints.down('xs')]: {
                maxWidth: 120,
                maxHeight: 60
              },
              [theme.breakpoints.down(359)]: {
                maxWidth: 90
              }
            }
          }
        }
      },
      logoWrapper: {
        display: 'flex',
        flex: 1,
        height: '100%',
        '& img': {
          transition: 'all 1s cubic-bezier(0.46, 0.03, 0.52, 0.96)',
          height: 'auto',
          width: 'auto',
          padding: theme.spacing(1, 0),
          objectFit: 'contain',
          maxWidth: 200,
          maxHeight: 116,
          [theme.breakpoints.down('xs')]: {
            maxWidth: 140,
            maxHeight: 77
          },
          [theme.breakpoints.down(359)]: {
            maxWidth: 100
          }
        }
      },
      isDummy: {
        width: '100%',
        position: 'relative',
        zIndex: 1,
        paddingRight: `0 !important`,
        transition: 'all .2s ease-out',
        '&:hover': {
          filter: 'grayscale(.9)',
          '&::after': {
            backgroundColor: 'rgba(255, 255, 255, .7)'
          }
        },
        // after pseudo element
        '&::after': {
          transition: 'all .2s ease-out',
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%'
        }
      },
      toolbar: {
        boxSizing: 'border-box',
        margin: '0 auto',
        width: '100%',
        maxWidth: PRESENTATION_DEVICE_WIDTH.desktop,
        padding: theme.spacing(0, 4),
        minHeight: theme.paintscout.header.height.md,
        [theme.breakpoints.down('sm')]: {
          padding: theme.spacing(0, 2)
        }
      },
      paper: {
        width: 280,
        maxWidth: '82%',
        boxShadow: '6px 0 24px rgba(16, 24, 40, 0.18)',
        ['@media print']: {
          display: 'none'
        }
      },
      modal: {
        ['@media print']: {
          display: 'none'
        }
      },
      closedContainer: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        width: '100%'
      },
      noCart: {
        flexDirection: 'row-reverse',
        '& $menuButtonContent': {
          justifyContent: 'flex-end'
        }
      },
      left: {
        flex: '1 0 0',
        textAlign: 'left',
        display: 'flex',
        height: '100%'
      },
      center: {
        flex: '1 0 0',
        textAlign: 'center',
        display: 'flex',
        height: '100%',
        '& $logoWrapper': {
          justifyContent: 'center'
        }
      },
      right: {
        flex: '1 0 0',
        textAlign: 'right'
      },
      menuButtonContent: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        height: '100%'
      },
      dummyMenu: {
        fill: theme.palette.common.black,
        height: '100%'
      },
      shrunk: {},
      drawerCloseWrapper: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center'
      },
      acceptOnRightRoot: {
        flexDirection: 'row-reverse'
      },
      acceptOnRightMoreButton: {
        marginLeft: 0,
        marginRight: 12
      },
      hide: {
        display: 'none'
      },
      [theme.breakpoints.down('md')]: {
        acceptOnRightMoreButton: {
          marginRight: 8
        }
      }
    }
  },
  { name: 'PresentationNavMobile' }
)

export interface PresentationNavMobileProps extends WithStyles<any> {
  banner?: React.ReactNode
  presentation?: PresentationOptionV2
}

export default function PresentationNavMobile(props: PresentationNavMobileProps) {
  const classes = useStyles(props)
  const { banner } = props

  const { options } = useClientOptions()

  const [_currentPageKey, setCurrentPageKey] = useState()

  const { isDummy, isQuoteEmpty, onClose, onOpen, open, presentation } = usePresentationNav()
  const { window: win, document: doc } = useFrame()
  const { quote } = useQuote()
  const objectLabels = getObjectLabels({ options, quote, invoice: quote.is_invoice })
  const { logo } = getPresentationLogo({ options, quote, presentation })

  const [isShrunk, setShrunk] = useState(false)
  useEffect(() => {
    if (!isDummy) {
      const onScroll = () => {
        setShrunk((isShrunk) => {
          if (!isShrunk && (doc.body.scrollTop > 20 || doc.documentElement.scrollTop > 20)) {
            return true
          }

          if (isShrunk && doc.body.scrollTop < 4 && doc.documentElement.scrollTop < 4) {
            return false
          }

          return isShrunk
        })
      }

      win.addEventListener('scroll', onScroll)
      return () => win.removeEventListener('scroll', onScroll)
    }
  }, [])

  // useCart is a structural choice baked in at send time — read it from the quote's own
  // presentationTemplate snapshot, not the live "current" template `presentation` resolves to (see
  // PresentationNavFull for the same reasoning).
  const useCart = quote.presentationTemplate?.advanced?.useCart

  // page tabs when multi-page, section titles when single-page — see getPresentationNavItems
  const activeItems = getPresentationNavItems({ presentation, isQuoteEmpty })

  const isDocumentOnly = presentation.key === 'default'
  const mdDown = useMediaQuery('md')
  const isTwoCol = isDocumentOnly && mdDown

  return (
    <>
      <AppBar
        position="fixed"
        classes={{
          root: classnames({
            [classes.appBar]: true,
            [classes.isDummy]: isDummy,
            [classes.shrunk]: isShrunk
          })
        }}
      >
        {banner}
        <Toolbar classes={{ root: classes.toolbar }}>
          <div className={classes.closedContainer}>
            <div className={isTwoCol ? classes.hide : classes.left}>
              {isDummy ? (
                <HamburgerIcon className={classes.dummyMenu} />
              ) : (
                <MenuButton
                  activeItems={activeItems}
                  classes={classes}
                  onChange={(id) => {
                    setCurrentPageKey(id)
                  }}
                  onOpen={onOpen}
                  presentation={presentation}
                  useCart={useCart}
                />
              )}
            </div>
            <div className={isTwoCol ? classes.left : classes.center}>
              <div className={classes.logoWrapper}>
                <img src={logo} />
              </div>
            </div>
            <div className={`${classes.right} ${classes.drawerCloseWrapper}`}>
              {useCart &&
                !isQuoteEmpty &&
                (isDummy ? (
                  <Button variant={'dialog'}>{`View ${objectLabels.quote.value}`}</Button>
                ) : (
                  <PresentationQuoteDrawer />
                ))}
              {/*
                Quick Cart off means the estimate renders inline as a `quote` block, so this bar is
                the only place a customer can respond — the cart drawer that would otherwise hold
                the accept button never mounts. Gate on `!useCart` rather than the old
                `isDocumentOnly` (`presentation.key === 'default'`), which a v2 document can never
                satisfy: `useV2` requires the key to live in `presentationOptionsV2`, and 'default'
                is v1's injected document-only pseudo-presentation. `!useCart` subsumes it, since
                that pseudo-presentation carries no `advanced.useCart`. PresentationNavFull holds
                the same condition. Skip it for `isDummy` — the editor canvas nav passes no
                `onAcceptClick`, so the button would render empty anyway.
              */}
              {!useCart && !isDummy && (
                <PresentationAcceptButton
                  classes={{
                    acceptGridRoot: classes.acceptOnRightRoot,
                    moreButton: classes.acceptOnRightMoreButton
                  }}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left'
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left'
                  }}
                />
              )}
            </div>
          </div>
        </Toolbar>
      </AppBar>
      <Drawer
        disablePortal
        open={open}
        onClose={onClose}
        classes={{ paper: classes.paper, modal: classes.modal }}
        anchor={'left'}
      >
        <div className={classes.drawerPanel}>
          <div className={classes.drawerHeader}>
            <span className={classes.drawerTitle}>Pages</span>
            <CloseButton size="small" onCancel={onClose} className={classes.drawerCloseButton} />
          </div>
          <div className={classes.drawerBody}>
            <PresentationNavText presentation={presentation} />
          </div>
        </div>
      </Drawer>
    </>
  )
}

function MenuButton({ activeItems, classes, onChange, onOpen, presentation, useCart }) {
  if (presentation.key === 'default') return null

  return (
    <Scrollspy
      componentTag={'div'}
      style={{ width: '100%' }}
      items={activeItems.map((item) => item.key)}
      offset={-150}
      onUpdate={(element) => {
        onChange(element?.id)
      }}
    >
      <div className={classes.menuButtonContent}>
        <IconButton onClick={onOpen} edge={useCart ? 'start' : 'end'}>
          <HamburgerIcon />
        </IconButton>
      </div>
    </Scrollspy>
  )
}
